import { Button } from '@travelnest/merlin.button';
import * as React from 'react';

import zxcvbn from 'zxcvbn';
import { ICustomer } from '../../../../data/entities/customer';
import { SaveState } from '../../../../types';
import { Accordion, AccordionHeader, AccordionItem } from '../../../components/accordion';
import { Form, FormLabel, FormLabelHelp, FormRow } from '../../../components/form';
import { FormInputPassword } from '../../../components/form/form-input/FormInputPassword';
import { Loader } from '../../../components/loader/Loader';
import { Panel } from '../../../components/panel/Panel';
import { Paragraph } from '../../../components/paragraph/Paragraph';

interface IPersonalDetailsProps {
	customer: ICustomer;
	resettingPassword: { state: SaveState, context: {} | Error };
	onChange: (key: keyof ICustomer, value: any) => any;
	onPasswordReset(customer: ICustomer, from: string, to: string): any;
}

interface IPasswordPageState {
	customer: ICustomer;
	validCurrentPassword: boolean;
	validNewPassword: boolean;
	current_password: string;
	new_password1: string;
	new_password2: string;
}

export class CustomerPasswordEdit extends React.Component<IPersonalDetailsProps, IPasswordPageState> {
	public componentWillMount() {
		this.setState({
			validCurrentPassword: false,
			validNewPassword: false
		});
	}

	public onChange(key: string, value: string) {
		const state = this.state || {} as IPasswordPageState;
		let validCurrentPassword = state.validCurrentPassword;
		let validNewPassword = state.validNewPassword;
		const { current_password, new_password1, new_password2 } = state;
		if (key === 'current_password') {
			validCurrentPassword = !(value && new_password1) || value !== new_password1;
		} else if (key === 'new_password1') {
			validNewPassword = (!(value && new_password2) || value === new_password2);
			validCurrentPassword = !(value && current_password) || value !== current_password;
		} else if (key === 'new_password2') {
			validNewPassword = (!(new_password1 && value) || new_password1 === value);
		}
		this.setState({ ...state, [key]: value, validCurrentPassword, validNewPassword });
	}

	public submit() {
		const state = this.state || {} as IPasswordPageState;
		this.props.onPasswordReset(this.props.customer, state.current_password, state.new_password1);
	}

	public passMeter(password: string) {
		switch (password && password.length && zxcvbn(password).score) {
			case 2:
				return 'fair';
			case 3:
				return 'good';
			case 4:
				return 'strong';
			default:
				return 'weak';
		}
	}

	public passwordCheck(password: string) {
		return {
			length: password && password.length > 7,
			strength: password && password.length && zxcvbn(password).score > 2
		};
	}

	public errorMessage(error: {} | Error) {
		if (('message' in error)) {
			if (error.message.includes('password cannot be used for security reasons')) {
				return 'Your new password is potentially insecure, please try another';
			}
		}
		return 'Could not save information. Make sure your current password is correct and your new password matches the criteria specified below.';
	}

	public render() {
		const { customer, resettingPassword } = this.props;
		const { validCurrentPassword, validNewPassword, current_password, new_password1, new_password2 } = this.state || {} as IPasswordPageState;
		const changeCurrPwd = this.onChange.bind(this, 'current_password');
		const changeNewPwd1 = this.onChange.bind(this, 'new_password1');
		const changeNewPwd2 = this.onChange.bind(this, 'new_password2');
		const passwordCheck = this.passwordCheck(new_password1);
		const passwordStrength = this.passMeter(new_password1);

		return !customer ? <Loader loading={true} /> : (
			<div className="customer-details">
				<Accordion>
					<AccordionHeader>Update password</AccordionHeader>
					<div className="customer-personal-details__panel-message">
						{(resettingPassword.state === SaveState.SUCCESS) &&
							<Panel type="default">Your password has been reset</Panel>
						}
						{(resettingPassword.state === SaveState.FAILURE) &&
							<Panel type="error">{this.errorMessage(resettingPassword.context)}</Panel>
						}
						{!validCurrentPassword && current_password && new_password1 &&
							<Panel type="error">
								Your new password is the same as your current passsord
							</Panel>
						}
						{!validNewPassword && new_password1 && new_password2 &&
							<Panel type="error">
								Your new passwords do not match
							</Panel>
						}
					</div>
					<AccordionItem>
						<Paragraph fillParent="mid">To begin with, enter your current password.</Paragraph>
						<Form onSubmit={(e) => { e.preventDefault(); this.submit(); }}>

							<FormRow>
								<FormLabel htmlFor="current_password">Current password</FormLabel>
								<FormInputPassword
									onChange={(e) => changeCurrPwd(e.target.value)}
									name="current_password"
									id="current_password"
									required
								/>
							</FormRow>

							<Paragraph fillParent="mid">Now enter your new password.</Paragraph>

							<div className="checklist checklist--success checklist--item">
								<div className="checklist--item_content">
									<img src={`/71eb904/assets/icons/messages/${passwordCheck.length ? 'success' : 'pending'}.svg`}></img>
									<div className="checklist--item_text">Your password has to be at least 8 characters long.</div>
								</div>
							</div>
							<div className="checklist checklist--success checklist--item">
								<div className="checklist--item_content">
									<img src={`/71eb904/assets/icons/messages/${passwordCheck.strength ? 'success' : 'pending'}.svg`}></img>
									<div className="checklist--item_text">Your password is {passwordStrength}.</div>
								</div>
							</div>

							<FormRow>
								<FormLabel htmlFor="new_password1">New Password</FormLabel>
								<FormInputPassword
									onChange={(e) => changeNewPwd1(e.target.value)}
									name="new_password1"
									id="new_password1"
									required
								/>
							</FormRow>

							<FormRow>
								<FormLabel htmlFor="new_password2">Enter new password again</FormLabel>
								<FormLabelHelp>Make sure it matches what you have entered above</FormLabelHelp>
								<FormInputPassword
									onChange={(e) => changeNewPwd2(e.target.value)}
									name="new_password2"
									id="new_password2"
									required
								/>
							</FormRow>

							<Button disabled={!(validCurrentPassword && validNewPassword && current_password && new_password1 && new_password2)}>Update password</Button>
						</Form>
					</AccordionItem>
				</Accordion>
			</div>
		);
	}
}
